import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Search, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Contact, Lead } from "@lumika/shared";
import {
  Button,
  ContactAvatar,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from "@lumika/ui";
import { bdcApi } from "../api/bdc";
import { contactDisplayName } from "../lib/contact";

type LeadStatus = Lead["status"];
type LeadType = NonNullable<Lead["type"]>;

const INITIAL_STAGES: Array<{ value: LeadStatus; label: string }> = [
  { value: "new", label: "New" },
  { value: "working", label: "Working" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

export function NewLeadDialog({
  open,
  onOpenChange,
  onStartConversation,
  presetContact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartConversation: (contact: Contact, lead: Lead) => void;
  /** Contact-owned entry points lock the Lead to their current Contact. */
  presetContact?: Contact;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [contact, setContact] = useState<Contact | null>(presetContact ?? null);
  const [type, setType] = useState<LeadType>("sales");
  const [interest, setInterest] = useState("");
  const [rooftopId, setRooftopId] = useState("unassigned");
  const [ownerId, setOwnerId] = useState("unassigned");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState<LeadStatus>("new");
  const [lostReason, setLostReason] = useState("");
  const [createdLead, setCreatedLead] = useState<Lead | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (open) {
      if (presetContact) {
        setContact(presetContact);
        setSource(presetContact.leadSource ?? "");
        setOwnerId(presetContact.assignedUserId ?? "unassigned");
      }
      return;
    }
    setSearch("");
    setDebouncedSearch("");
    setContact(presetContact ?? null);
    setType("sales");
    setInterest("");
    setRooftopId("unassigned");
    setOwnerId(presetContact?.assignedUserId ?? "unassigned");
    setSource(presetContact?.leadSource ?? "");
    setStatus("new");
    setLostReason("");
    setCreatedLead(null);
  }, [open, presetContact]);

  const contactsQuery = useQuery({
    queryKey: ["bdc", "new-lead-contacts", debouncedSearch],
    queryFn: () =>
      bdcApi.contacts({
        search: debouncedSearch || undefined,
        page: 1,
        pageSize: 8,
      }),
    enabled: open && !contact,
  });
  const usersQuery = useQuery({
    queryKey: ["bdc", "users"],
    queryFn: bdcApi.users,
    enabled: open,
  });
  const rooftopsQuery = useQuery({
    queryKey: ["bdc", "rooftops"],
    queryFn: bdcApi.rooftops,
    enabled: open,
  });
  const sourcesQuery = useQuery({
    queryKey: ["bdc", "contact-lead-sources"],
    queryFn: bdcApi.contactLeadSources,
    enabled: open,
  });

  const createLead = useMutation({
    mutationFn: () => {
      if (!contact) throw new Error("Choose a contact first.");
      if (status === "lost" && !lostReason.trim()) {
        throw new Error("Add a reason before creating a Lost lead.");
      }
      return bdcApi.createLead({
        contactId: contact.id,
        primaryChannel: null,
        rooftopId: rooftopId === "unassigned" ? null : rooftopId,
        type,
        status,
        lostReason: status === "lost" ? lostReason.trim() : null,
        interest: interest.trim() || null,
        assignedUserId: ownerId === "unassigned" ? null : ownerId,
        source: source.trim() || null,
        closed: false,
      });
    },
    onSuccess: async (lead) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bdc", "leads"] }),
        queryClient.invalidateQueries({ queryKey: ["bdc", "contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["bdc", "contact-lead-sources"] }),
      ]);
      setCreatedLead(lead);
      toast.success("Lead created");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function chooseContact(nextContact: Contact) {
    setContact(nextContact);
    setSource(nextContact.leadSource ?? "");
    setOwnerId(nextContact.assignedUserId ?? "unassigned");
  }

  const canCreate =
    contact !== null &&
    !createLead.isPending &&
    (status !== "lost" || lostReason.trim().length > 0);
  const isDirty =
    contact !== (presetContact ?? null) ||
    type !== "sales" ||
    interest !== "" ||
    rooftopId !== "unassigned" ||
    ownerId !== (presetContact?.assignedUserId ?? "unassigned") ||
    source !== (presetContact?.leadSource ?? "") ||
    status !== "new" ||
    lostReason !== "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        preventDismiss={createLead.isPending || (!createdLead && isDirty)}
        className="sm:max-w-2xl"
      >
        {createdLead && contact ? (
          <>
            <DialogHeader>
              <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-success/10 text-success">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <DialogTitle>Lead created</DialogTitle>
              <DialogDescription>
                {contactDisplayName(contact)} is now in {INITIAL_STAGES.find((item) => item.value === createdLead.status)?.label ?? createdLead.status}. No conversation was started.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/inbox/${createdLead.id}`);
                }}
              >
                Open lead
              </Button>
              <Button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onStartConversation(contact, createdLead);
                }}
              >
                Start conversation
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>New lead</DialogTitle>
              <DialogDescription>
                Create the opportunity first. Messaging and calling stay separate until you choose to start a conversation.
              </DialogDescription>
            </DialogHeader>

            <DialogBody>
              <form
                id="new-lead-form"
                className="space-y-5"
                onSubmit={(event) => {
                  event.preventDefault();
                  createLead.mutate();
                }}
              >
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">Contact</legend>
                  {contact ? (
                    <div className="flex items-center justify-between gap-3 border-y py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <ContactAvatar
                          name={contactDisplayName(contact)}
                          avatarUrl={contact.avatarUrl}
                          size="sm"
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {contactDisplayName(contact)}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {contact.primaryEmail ?? contact.primaryPhone ?? "No contact method"}
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className={presetContact ? "hidden" : undefined}
                        onClick={() => setContact(null)}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          autoFocus
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          placeholder="Search by name, phone, or email"
                          aria-label="Search contacts"
                          className="pl-9"
                        />
                      </div>
                      <div className="max-h-48 divide-y overflow-y-auto border-y">
                        {contactsQuery.isLoading ? (
                          <p className="px-1 py-4 text-sm text-muted-foreground">
                            Loading contacts…
                          </p>
                        ) : (contactsQuery.data?.items.length ?? 0) === 0 ? (
                          <p className="px-1 py-4 text-sm text-muted-foreground">
                            No contacts found. Create the Contact before creating its Lead.
                          </p>
                        ) : (
                          contactsQuery.data?.items.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => chooseContact(item)}
                              className="flex min-h-12 w-full items-center gap-3 px-1 py-2 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                            >
                              <ContactAvatar
                                name={contactDisplayName(item)}
                                avatarUrl={item.avatarUrl}
                                size="sm"
                              />
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-medium">
                                  {contactDisplayName(item)}
                                </span>
                                <span className="block truncate text-xs text-muted-foreground">
                                  {item.primaryEmail ?? item.primaryPhone ?? "No contact method"}
                                </span>
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </fieldset>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="new-lead-type">Type</Label>
                    <Select value={type} onValueChange={(value) => setType(value as LeadType)}>
                      <SelectTrigger id="new-lead-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sales">Sales</SelectItem>
                        <SelectItem value="service">Service</SelectItem>
                        <SelectItem value="parts">Parts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-lead-stage">Initial stage</Label>
                    <Select value={status} onValueChange={(value) => setStatus(value as LeadStatus)}>
                      <SelectTrigger id="new-lead-stage">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INITIAL_STAGES.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Appointment is set automatically after an appointment is booked.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-lead-location">Location</Label>
                    <Select value={rooftopId} onValueChange={setRooftopId}>
                      <SelectTrigger id="new-lead-location">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">No location</SelectItem>
                        {(rooftopsQuery.data ?? [])
                          .filter((rooftop) => rooftop.status === "active")
                          .map((rooftop) => (
                            <SelectItem key={rooftop.id} value={rooftop.id}>
                              {rooftop.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-lead-owner">Owner</Label>
                    <Select value={ownerId} onValueChange={setOwnerId}>
                      <SelectTrigger id="new-lead-owner">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {(usersQuery.data ?? []).map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.displayName ?? user.email ?? user.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-lead-interest">Vehicle or interest</Label>
                  <Input
                    id="new-lead-interest"
                    value={interest}
                    onChange={(event) => setInterest(event.target.value)}
                    placeholder="For example, 2025 RAV4 Hybrid or 30k-mile service"
                    maxLength={500}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-lead-source">Source</Label>
                  <Input
                    id="new-lead-source"
                    value={source}
                    onChange={(event) => setSource(event.target.value)}
                    placeholder="For example, Website or Referral"
                    list="new-lead-source-options"
                    maxLength={200}
                  />
                  <datalist id="new-lead-source-options">
                    {(sourcesQuery.data ?? []).map((item) => (
                      <option key={item} value={item} />
                    ))}
                  </datalist>
                  <p className="text-xs text-muted-foreground">
                    Source is shared with the selected Contact.
                  </p>
                </div>

                {status === "lost" ? (
                  <div className="space-y-2">
                    <Label htmlFor="new-lead-lost-reason">Lost reason</Label>
                    <Textarea
                      id="new-lead-lost-reason"
                      value={lostReason}
                      onChange={(event) => setLostReason(event.target.value)}
                      placeholder="Why was this opportunity lost?"
                      maxLength={500}
                      required
                    />
                  </div>
                ) : null}

                {contact ? (
                  <p className="flex items-center gap-2 text-xs text-muted-foreground">
                    <UserRound className="h-3.5 w-3.5" />
                    Creating this Lead will not send a message or place a call.
                  </p>
                ) : null}
              </form>
            </DialogBody>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={createLead.isPending}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="new-lead-form"
                disabled={!canCreate}
              >
                {createLead.isPending ? "Creating…" : "Create lead"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
